import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getVendor, updateVendor,
  createVendorGLKeyword, updateVendorGLKeyword, deleteVendorGLKeyword,
  getVendorJobKeywords, createVendorJobKeyword, updateVendorJobKeyword, deleteVendorJobKeyword,
  getItemLookup, createItemLookup, updateItemLookup, deleteItemLookup,
} from '../../adminApi';
import { Vendor, VendorGLKeyword, VendorJobKeyword, ItemLookup } from '../../adminTypes';
import { getGLAccounts, getJobs, getCreditCards } from '../../api';
import { GLAccount, Job, CreditCard } from '../../types';

// ── Workflow Type Display Labels ───────────────────────────────────────────

const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  US_FOODS: 'Special Processing - US Foods',
  GFS: 'Special Processing - GFS',
  STAPLES: 'Special Processing - Staples',
  OTHER: 'Standard',
};
const getWorkflowTypeLabel = (type: string) => WORKFLOW_TYPE_LABELS[type] || type;

// ── Types ─────────────────────────────────────────────────────────────────

interface VendorDetailPanelProps {
  vendor: Vendor;
  onClose: () => void;
  onVendorSaved: (updated: Vendor) => void;
}

interface VendorEdits {
  sender_emails: string[];
  name_keywords: string[];
  default_payment_method_id: number;
  default_cc_vendor_number: number | null;
  workflow_type: string;
  job_resolution: 'default' | 'one_per_invoice' | 'line_item';
  gl_resolution: 'single' | 'multiple';
  default_job_number: string;
  default_gl_number: string;
  processing_instructions: string;  // plain-text AI notes; stored in DB as {"notes": "..."}
  active: boolean;
}

interface GLKeywordForm {
  keyword: string;
  match_operator: 'equals' | 'contains' | 'starts_with' | 'regex';
  gl_number: string;
  priority: number;
  active: boolean;
}

interface JobKeywordForm {
  keyword: string;
  keywordvalue: string;
  match_operator: 'equals' | 'contains' | 'starts_with' | 'regex';
  job_number: string;
  priority: number;
  notes: string;
  active: boolean;
}

interface PartForm {
  part_number: string;
  description: string;
  gl_number: string;
  category: string;
  job_number: string;
  active: boolean;
}

const EMPTY_KW_FORM: GLKeywordForm = {
  keyword: '', match_operator: 'equals', gl_number: '', priority: 100, active: true,
};

const EMPTY_JK_FORM: JobKeywordForm = {
  keyword: '', keywordvalue: '', match_operator: 'contains', job_number: '', priority: 100, notes: '', active: true,
};

const EMPTY_PART_FORM: PartForm = {
  part_number: '', description: '', gl_number: '', category: '', job_number: '', active: true,
};

// ── Styles ────────────────────────────────────────────────────────────────

const NAVY = '#00386C';

const sectionHeaderStyle: React.CSSProperties = {
  backgroundColor: NAVY,
  color: 'white',
  padding: '10px 16px',
  fontSize: '14px',
  fontWeight: 'bold',
  letterSpacing: '0.3px',
  borderRadius: '4px 4px 0 0',
  marginBottom: 0,
};

const sectionBodyStyle: React.CSSProperties = {
  border: '1px solid #d0dbe8',
  borderTop: 'none',
  borderRadius: '0 0 4px 4px',
  padding: '16px',
  backgroundColor: 'white',
  marginBottom: '24px',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontWeight: 'bold',
  fontSize: '12px',
  marginBottom: '4px',
  color: '#374151',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid #ccc',
  borderRadius: '3px',
  fontSize: '13px',
  boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  padding: '7px 14px',
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '3px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 'bold',
};

const btnSecondary: React.CSSProperties = {
  padding: '7px 14px',
  backgroundColor: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: '3px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 'bold',
};

const btnDanger: React.CSSProperties = {
  padding: '4px 8px',
  backgroundColor: '#dc3545',
  color: 'white',
  border: 'none',
  borderRadius: '2px',
  cursor: 'pointer',
  fontSize: '11px',
};

const btnEdit: React.CSSProperties = {
  padding: '4px 8px',
  backgroundColor: '#007bff',
  color: 'white',
  border: 'none',
  borderRadius: '2px',
  cursor: 'pointer',
  fontSize: '11px',
  marginRight: '4px',
};

const subTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '12px',
  marginBottom: '12px',
  backgroundColor: 'white',
  border: '1px solid #ddd',
};

const thStyle: React.CSSProperties = {
  padding: '7px 8px',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: 'bold',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #ddd',
};

const tdStyle: React.CSSProperties = {
  padding: '7px 8px',
  borderBottom: '1px solid #eee',
  verticalAlign: 'middle',
};

const activeDot = (active: boolean) => ({
  display: 'inline-block',
  width: '16px',
  height: '16px',
  borderRadius: '8px',
  backgroundColor: active ? '#4CAF50' : '#ccc',
  color: 'white',
  textAlign: 'center' as const,
  lineHeight: '16px',
  fontSize: '10px',
  fontWeight: 'bold',
});

const inlineFormStyle: React.CSSProperties = {
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '4px',
  padding: '14px',
  marginTop: '12px',
};

// ── TypeaheadInput ────────────────────────────────────────────────────────

interface TypeaheadOption { value: string; label: string; }

interface TypeaheadInputProps {
  value: string;
  options: TypeaheadOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

const TypeaheadInput: React.FC<TypeaheadInputProps> = ({ value, options, onChange, placeholder, style }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const selected = options.find(o => o.value === value);
  const displayValue = focused ? query : (selected ? selected.label : value);

  const filtered = query.length === 0
    ? options.slice(0, 50)
    : options.filter(o =>
        o.value.toLowerCase().includes(query.toLowerCase()) ||
        o.label.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 50);

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={displayValue}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => { setFocused(true); setQuery(''); setOpen(true); }}
        onBlur={() => setTimeout(() => {
          // If the user typed a value without clicking a dropdown option, try to
          // accept it on blur.  Exact match on value (e.g. "4091") or label wins;
          // partial matches are ignored so we don't silently pick the wrong GL.
          if (query) {
            const q = query.trim().toLowerCase();
            const match = options.find(
              o => o.value.toLowerCase() === q || o.label.toLowerCase() === q
            );
            if (match) onChange(match.value);
          }
          setFocused(false);
          setOpen(false);
        }, 150)}
        placeholder={placeholder}
        style={style}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          backgroundColor: 'white', border: '1px solid #cbd5e0',
          borderRadius: '4px', zIndex: 1000, maxHeight: '220px',
          overflowY: 'auto', boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
        }}>
          {filtered.map(opt => (
            <div
              key={opt.value}
              onMouseDown={() => { onChange(opt.value); setQuery(''); setOpen(false); setFocused(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f3f4f6' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f0f7ff')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Component ─────────────────────────────────────────────────────────────

const VendorDetailPanel: React.FC<VendorDetailPanelProps> = ({ vendor, onClose, onVendorSaved }) => {

  // Vendor Info
  const [edits, setEdits] = useState<VendorEdits>({
    sender_emails: vendor.sender_emails || [],
    name_keywords: vendor.name_keywords || [],
    default_payment_method_id: vendor.default_payment_method_id || 1,
    default_cc_vendor_number: vendor.default_cc_vendor_number ?? null,
    workflow_type: vendor.workflow_type || 'OTHER',
    job_resolution: (['default', 'one_per_invoice', 'line_item'].includes(vendor.job_resolution) ? vendor.job_resolution as VendorEdits['job_resolution'] : 'default'),
    gl_resolution: (['single', 'multiple'].includes(vendor.gl_resolution) ? vendor.gl_resolution as VendorEdits['gl_resolution'] : 'single'),
    default_job_number: vendor.default_job_number || '',
    default_gl_number: vendor.default_gl_number || '',
    processing_instructions: (vendor.processing_instructions as any)?.notes || '',
    active: vendor.active !== false,
  });
  const [savingVendor, setSavingVendor] = useState(false);

  // Lookup data for typeaheads
  const [jobs, setJobs] = useState<Job[]>([]);
  const [glAccounts, setGLAccounts] = useState<GLAccount[]>([]);
  const [creditCards, setCreditCards] = useState<CreditCard[]>([]);

  // GL Keywords
  const [glKeywords, setGLKeywords] = useState<VendorGLKeyword[]>([]);
  const [glLoading, setGLLoading] = useState(true);
  const [kwForm, setKwForm] = useState<GLKeywordForm>(EMPTY_KW_FORM);
  const [editingKwId, setEditingKwId] = useState<number | null>(null);
  const [savingKw, setSavingKw] = useState(false);

  // Job Keywords
  const [jobKeywords, setJobKeywords] = useState<VendorJobKeyword[]>([]);
  const [jkLoading, setJKLoading] = useState(true);
  const [jkForm, setJKForm] = useState<JobKeywordForm>(EMPTY_JK_FORM);
  const [editingJKId, setEditingJKId] = useState<number | null>(null);
  const [savingJK, setSavingJK] = useState(false);

  // Parts
  const [parts, setParts] = useState<ItemLookup[]>([]);
  const [partsLoading, setPartsLoading] = useState(true);
  const [partsSearch, setPartsSearch] = useState('');
  const [partForm, setPartForm] = useState<PartForm>(EMPTY_PART_FORM);
  const [editingPartId, setEditingPartId] = useState<number | null>(null);
  const [showPartForm, setShowPartForm] = useState(false);
  const [savingPart, setSavingPart] = useState(false);
  const [importingCSV, setImportingCSV] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const csvInputRef = useRef<HTMLInputElement>(null);

  // ── Data Loading ────────────────────────────────────────────────────────

  const loadGLKeywords = useCallback(async () => {
    setGLLoading(true);
    try {
      const detail = await getVendor(vendor.vendor_number);
      setGLKeywords(detail.gl_keywords || []);
    } catch {
      showToast('Failed to load GL keywords', 'error');
    } finally {
      setGLLoading(false);
    }
  }, [vendor.vendor_number]);

  const loadJobKeywords = useCallback(async () => {
    setJKLoading(true);
    try {
      const data = await getVendorJobKeywords(vendor.vendor_number);
      setJobKeywords(data);
    } catch {
      showToast('Failed to load job keywords', 'error');
    } finally {
      setJKLoading(false);
    }
  }, [vendor.vendor_number]);

  const loadParts = useCallback(async () => {
    setPartsLoading(true);
    try {
      const data = await getItemLookup(vendor.vendor_number);
      setParts(data);
    } catch {
      showToast('Failed to load parts', 'error');
    } finally {
      setPartsLoading(false);
    }
  }, [vendor.vendor_number]);

  useEffect(() => {
    loadGLKeywords();
    loadJobKeywords();
    loadParts();
    getJobs().then(setJobs).catch(() => {});
    getGLAccounts().then(setGLAccounts).catch(() => {});
    getCreditCards().then(setCreditCards).catch(() => {});
  }, [loadGLKeywords, loadJobKeywords, loadParts]);

  // ESC to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ── Toast ───────────────────────────────────────────────────────────────

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Vendor Info ─────────────────────────────────────────────────────────

  const handleSaveVendor = async () => {
    setSavingVendor(true);
    try {
      const updated = await updateVendor(vendor.vendor_number, {
        sender_emails: edits.sender_emails,
        name_keywords: edits.name_keywords,
        default_payment_method_id: edits.default_payment_method_id,
        default_cc_vendor_number: edits.default_payment_method_id === 2 ? edits.default_cc_vendor_number : null,
        workflow_type: edits.workflow_type,
        job_resolution: edits.job_resolution,
        gl_resolution: edits.gl_resolution,
        default_job_number: edits.default_job_number.trim() || null,
        default_gl_number: edits.default_gl_number.trim() || null,
        processing_instructions: edits.processing_instructions.trim()
          ? { notes: edits.processing_instructions.trim() }
          : {},
        active: edits.active,
      });
      onVendorSaved(updated);
      showToast('Vendor saved', 'success');
    } catch {
      showToast('Failed to save vendor', 'error');
    } finally {
      setSavingVendor(false);
    }
  };

  // ── GL Keywords ─────────────────────────────────────────────────────────

  const handleSaveKw = async () => {
    if (!kwForm.keyword.trim() || !kwForm.gl_number.trim()) {
      showToast('Keyword and GL Number are required', 'error');
      return;
    }
    setSavingKw(true);
    try {
      if (editingKwId !== null) {
        await updateVendorGLKeyword(editingKwId, kwForm);
        showToast('Keyword updated', 'success');
      } else {
        await createVendorGLKeyword({ vendor_number: vendor.vendor_number, ...kwForm });
        showToast('Keyword added', 'success');
      }
      setKwForm(EMPTY_KW_FORM);
      setEditingKwId(null);
      await loadGLKeywords();
    } catch {
      showToast('Failed to save keyword', 'error');
    } finally {
      setSavingKw(false);
    }
  };

  const handleEditKw = (kw: VendorGLKeyword) => {
    setKwForm({
      keyword: kw.keyword,
      match_operator: kw.match_operator,
      gl_number: kw.gl_number,
      priority: kw.priority,
      active: kw.active,
    });
    setEditingKwId(kw.id);
  };

  const handleDeleteKw = async (id: number) => {
    if (!window.confirm('Delete this GL keyword rule?')) return;
    try {
      await deleteVendorGLKeyword(id);
      showToast('Keyword deleted', 'success');
      await loadGLKeywords();
    } catch {
      showToast('Failed to delete keyword', 'error');
    }
  };

  const handleCancelKw = () => {
    setKwForm(EMPTY_KW_FORM);
    setEditingKwId(null);
  };

  // ── Job Keywords ────────────────────────────────────────────────────────

  const handleSaveJK = async () => {
    if (!jkForm.keyword.trim() || !jkForm.keywordvalue.trim() || !jkForm.job_number.trim()) {
      showToast('Field Label, Value, and Job # are required', 'error');
      return;
    }
    setSavingJK(true);
    try {
      if (editingJKId !== null) {
        await updateVendorJobKeyword(editingJKId, jkForm);
        showToast('Job keyword updated', 'success');
      } else {
        await createVendorJobKeyword({ vendor_number: vendor.vendor_number, ...jkForm });
        showToast('Job keyword added', 'success');
      }
      setJKForm(EMPTY_JK_FORM);
      setEditingJKId(null);
      await loadJobKeywords();
    } catch {
      showToast('Failed to save job keyword', 'error');
    } finally {
      setSavingJK(false);
    }
  };

  const handleEditJK = (jk: VendorJobKeyword) => {
    setJKForm({
      keyword: jk.keyword,
      keywordvalue: jk.keywordvalue,
      match_operator: jk.match_operator,
      job_number: jk.job_number,
      priority: jk.priority,
      notes: jk.notes || '',
      active: jk.active,
    });
    setEditingJKId(jk.id);
  };

  const handleDeleteJK = async (id: number) => {
    if (!window.confirm('Delete this job keyword rule?')) return;
    try {
      await deleteVendorJobKeyword(id);
      showToast('Job keyword deleted', 'success');
      await loadJobKeywords();
    } catch {
      showToast('Failed to delete job keyword', 'error');
    }
  };

  const handleCancelJK = () => {
    setJKForm(EMPTY_JK_FORM);
    setEditingJKId(null);
  };

  // ── Parts ───────────────────────────────────────────────────────────────

  const filteredParts = parts.filter(p => {
    const q = partsSearch.toLowerCase();
    return (
      p.part_number.toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q)
    );
  });

  const handleSavePart = async () => {
    if (!partForm.part_number.trim() || !partForm.gl_number.trim()) {
      showToast('Part Number and GL Number are required', 'error');
      return;
    }
    setSavingPart(true);
    try {
      const payload = {
        vendor_number: vendor.vendor_number,
        part_number: partForm.part_number.trim(),
        description: partForm.description.trim() || null,
        gl_number: partForm.gl_number.trim(),
        category: partForm.category.trim() || null,
        job_number: partForm.job_number.trim() || null,
        active: partForm.active,
      };
      if (editingPartId !== null) {
        await updateItemLookup(editingPartId, payload);
        showToast('Part updated', 'success');
      } else {
        await createItemLookup(payload);
        showToast('Part added', 'success');
      }
      setPartForm(EMPTY_PART_FORM);
      setEditingPartId(null);
      setShowPartForm(false);
      await loadParts();
    } catch {
      showToast('Failed to save part', 'error');
    } finally {
      setSavingPart(false);
    }
  };

  const handleEditPart = (p: ItemLookup) => {
    setPartForm({
      part_number: p.part_number,
      description: p.description || '',
      gl_number: p.gl_number,
      category: p.category || '',
      job_number: p.job_number || '',
      active: p.active,
    });
    setEditingPartId(p.id);
    setShowPartForm(true);
  };

  const handleDeletePart = async (id: number) => {
    if (!window.confirm('Deactivate this part lookup entry?')) return;
    try {
      await deleteItemLookup(id);
      showToast('Part deactivated', 'success');
      await loadParts();
    } catch {
      showToast('Failed to deactivate part', 'error');
    }
  };

  const handleCancelPart = () => {
    setPartForm(EMPTY_PART_FORM);
    setEditingPartId(null);
    setShowPartForm(false);
  };

  // ── CSV Import ──────────────────────────────────────────────────────────

  const handleCSVImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingCSV(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').map(l => l.trim()).filter(l => l);
      if (lines.length < 2) {
        showToast('CSV must have a header row and at least one data row', 'error');
        return;
      }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
      const pnIdx = headers.indexOf('part_number');
      const glIdx = headers.indexOf('gl_number');
      const descIdx = headers.indexOf('description');
      const catIdx = headers.indexOf('category');
      const jobIdx = headers.indexOf('job_number');

      if (pnIdx === -1 || glIdx === -1) {
        showToast('CSV must have columns: part_number, gl_number (and optionally: description, category, job_number)', 'error');
        return;
      }

      let imported = 0;
      let errors = 0;
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim().replace(/"/g, ''));
        const pn = cols[pnIdx];
        const gl = cols[glIdx];
        if (!pn || !gl) { errors++; continue; }
        try {
          await createItemLookup({
            vendor_number: vendor.vendor_number,
            part_number: pn,
            gl_number: gl,
            description: descIdx !== -1 ? (cols[descIdx] || null) : null,
            category: catIdx !== -1 ? (cols[catIdx] || null) : null,
            job_number: jobIdx !== -1 ? (cols[jobIdx] || null) : null,
            active: true,
          });
          imported++;
        } catch { errors++; }
      }
      showToast(`Imported ${imported} parts${errors > 0 ? `, ${errors} errors` : ''}`, imported > 0 ? 'success' : 'error');
      await loadParts();
    } catch {
      showToast('Failed to read CSV file', 'error');
    } finally {
      setImportingCSV(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  // ── Derived flags ───────────────────────────────────────────────────────
  const isSpecialProcessing = ['US_FOODS', 'GFS', 'STAPLES'].includes(edits.workflow_type);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 1000,
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed', top: 0, right: 0,
          width: '800px', maxWidth: '95vw',
          height: '100vh',
          backgroundColor: '#f7f9fc',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
          zIndex: 1001,
          display: 'flex', flexDirection: 'column',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        {/* Panel Header */}
        <div
          style={{
            backgroundColor: NAVY,
            color: 'white',
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{vendor.vendor_name}</div>
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '2px' }}>
              Vendor #{vendor.vendor_number}
              {vendor.workflow_type && (
                <span style={{ marginLeft: '10px', background: 'rgba(255,255,255,0.2)', padding: '1px 7px', borderRadius: '10px' }}>
                  {getWorkflowTypeLabel(vendor.workflow_type)}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer',
              width: '34px', height: '34px',
              borderRadius: '4px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* ── Section 1: Vendor Info ── */}
          <div style={sectionHeaderStyle}>📋 Vendor Information</div>
          <div style={sectionBodyStyle}>
            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Sender Emails <span style={{ fontWeight: 'normal', color: '#888' }}>(comma-separated)</span></label>
              <input
                type="text"
                style={inputStyle}
                value={edits.sender_emails.join(', ')}
                onChange={e => setEdits({ ...edits, sender_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                placeholder="e.g. invoices@vendor.com, billing@vendor.com"
              />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Name Keywords <span style={{ fontWeight: 'normal', color: '#888' }}>(comma-separated — used to identify vendor from email subject/body)</span></label>
              <input
                type="text"
                style={inputStyle}
                value={edits.name_keywords.join(', ')}
                onChange={e => setEdits({ ...edits, name_keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                placeholder="e.g. US Foods, USF"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Default Payment Method</label>
                <select
                  style={{ ...inputStyle }}
                  value={edits.default_payment_method_id}
                  onChange={e => {
                    const id = parseInt(e.target.value);
                    setEdits({ ...edits, default_payment_method_id: id, default_cc_vendor_number: id === 2 ? edits.default_cc_vendor_number : null });
                  }}
                >
                  <option value={1}>Check</option>
                  <option value={2}>Credit Card</option>
                  <option value={3}>ACH / NACHA</option>
                  <option value={4}>EFT</option>
                </select>
                {edits.default_payment_method_id === 2 && (
                  <div style={{ marginTop: '10px' }}>
                    <label style={labelStyle}>Default Credit Card</label>
                    <select
                      style={{ ...inputStyle }}
                      value={edits.default_cc_vendor_number ?? ''}
                      onChange={e => setEdits({ ...edits, default_cc_vendor_number: e.target.value ? parseInt(e.target.value) : null })}
                    >
                      <option value="">— Select card —</option>
                      {creditCards.filter(cc => cc.active !== false).map(cc => (
                        <option key={cc.cc_vendor_number} value={cc.cc_vendor_number}>
                          {cc.description}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Workflow Type</label>
                <select
                  style={{ ...inputStyle }}
                  value={edits.workflow_type}
                  onChange={e => setEdits({ ...edits, workflow_type: e.target.value })}
                >
                  <option value="US_FOODS">Special Processing - US Foods</option>
                  <option value="GFS">Special Processing - GFS</option>
                  <option value="STAPLES">Special Processing - Staples</option>
                  <option value="OTHER">Standard</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Job Number Resolution</label>
                {isSpecialProcessing ? (
                  <>
                    <div style={{ ...inputStyle, backgroundColor: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed', display: 'flex', alignItems: 'center' }}>
                      One per invoice — job from invoice header
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>
                      Fixed for Special Processing vendors.
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <label style={{ ...labelStyle, color: '#b45309' }}>Fallback Job Number</label>
                      <TypeaheadInput
                        value={edits.default_job_number}
                        options={jobs.map(j => ({ value: j.job_number, label: `${j.job_number} — ${j.job_name}` }))}
                        onChange={val => setEdits({ ...edits, default_job_number: val })}
                        placeholder="Search by number or name…"
                        style={{ ...inputStyle, borderColor: '#d97706', backgroundColor: '#fffbeb' }}
                      />
                      <div style={{ fontSize: '11px', color: '#92400e', marginTop: '3px' }}>
                        Used only when system cannot resolve job from invoice or keywords.
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <select
                      style={{ ...inputStyle }}
                      value={edits.job_resolution}
                      onChange={e => setEdits({ ...edits, job_resolution: e.target.value as VendorEdits['job_resolution'] })}
                    >
                      <option value="default">Default — single override job number</option>
                      <option value="one_per_invoice">One per invoice — job from invoice header</option>
                      <option value="line_item">Line item — job per line from description</option>
                    </select>
                    {edits.job_resolution === 'default' && (
                      <div style={{ marginTop: '8px' }}>
                        <label style={{ ...labelStyle, color: '#b45309' }}>Default Job Number *</label>
                        <TypeaheadInput
                          value={edits.default_job_number}
                          options={jobs.map(j => ({ value: j.job_number, label: `${j.job_number} — ${j.job_name}` }))}
                          onChange={val => setEdits({ ...edits, default_job_number: val })}
                          placeholder="Search by number or name…"
                          style={{ ...inputStyle, borderColor: '#d97706', backgroundColor: '#fffbeb' }}
                        />
                        <div style={{ fontSize: '11px', color: '#92400e', marginTop: '3px' }}>
                          This job number is applied to every line — keyword table is ignored.
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div>
                <label style={labelStyle}>GL Number Resolution</label>
                {isSpecialProcessing ? (
                  <>
                    <div style={{ ...inputStyle, backgroundColor: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed', display: 'flex', alignItems: 'center' }}>
                      Multiple — GL resolved per line from keywords
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>
                      Fixed for Special Processing vendors.
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <label style={{ ...labelStyle, color: '#b45309' }}>Fallback GL Number</label>
                      <TypeaheadInput
                        value={edits.default_gl_number}
                        options={glAccounts.map(g => ({ value: g.gl_number, label: `${g.gl_number} — ${g.description}` }))}
                        onChange={val => setEdits({ ...edits, default_gl_number: val })}
                        placeholder="Search by number or description…"
                        style={{ ...inputStyle, borderColor: '#d97706', backgroundColor: '#fffbeb' }}
                      />
                      <div style={{ fontSize: '11px', color: '#92400e', marginTop: '3px' }}>
                        Used only when system cannot resolve GL from keywords.
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <select
                      style={{ ...inputStyle }}
                      value={edits.gl_resolution}
                      onChange={e => setEdits({ ...edits, gl_resolution: e.target.value as VendorEdits['gl_resolution'] })}
                    >
                      <option value="single">Single — override GL number for all lines</option>
                      <option value="multiple">Multiple — GL resolved per line from keywords</option>
                    </select>
                    {edits.gl_resolution === 'single' && (
                      <div style={{ marginTop: '8px' }}>
                        <label style={{ ...labelStyle, color: '#b45309' }}>Default GL Number *</label>
                        <TypeaheadInput
                          value={edits.default_gl_number}
                          options={glAccounts.map(g => ({ value: g.gl_number, label: `${g.gl_number} — ${g.description}` }))}
                          onChange={val => setEdits({ ...edits, default_gl_number: val })}
                          placeholder="Search by number or description…"
                          style={{ ...inputStyle, borderColor: '#d97706', backgroundColor: '#fffbeb' }}
                        />
                        <div style={{ fontSize: '11px', color: '#92400e', marginTop: '3px' }}>
                          This GL number is applied to every line — keyword table is ignored.
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={edits.active}
                  onChange={e => setEdits({ ...edits, active: e.target.checked })}
                  style={{ cursor: 'pointer', width: '15px', height: '15px' }}
                />
                <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#374151' }}>Active</span>
              </label>
            </div>

            <button
              onClick={handleSaveVendor}
              disabled={savingVendor}
              style={{ ...btnPrimary, opacity: savingVendor ? 0.6 : 1, cursor: savingVendor ? 'not-allowed' : 'pointer' }}
            >
              {savingVendor ? 'Saving...' : 'Save Vendor'}
            </button>
          </div>

          {/* ── Section 2: GL Keywords ── */}
          <div style={sectionHeaderStyle}>
            🔑 GL Keywords
            {!isSpecialProcessing && edits.gl_resolution === 'single' && (
              <span style={{ marginLeft: '10px', fontSize: '11px', fontWeight: 'normal', opacity: 0.85, background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '10px' }}>
                Single mode — override GL in use
              </span>
            )}
          </div>
          <div style={sectionBodyStyle}>
            {!isSpecialProcessing && edits.gl_resolution === 'single' && (
              <div style={{ padding: '10px 14px', backgroundColor: '#fffbeb', border: '1px solid #d97706', borderRadius: '4px', marginBottom: '12px', fontSize: '12px', color: '#92400e' }}>
                ⚙️ <strong>GL Resolution is set to Single.</strong> The override GL number set above is applied to every line. This keyword table is inactive and ignored during processing.
              </div>
            )}
            <div style={(!isSpecialProcessing && edits.gl_resolution === 'single') ? { opacity: 0.35, pointerEvents: 'none' } : {}}>
            {glLoading ? (
              <p style={{ color: '#666', fontSize: '13px' }}>Loading keywords...</p>
            ) : glKeywords.length === 0 ? (
              <p style={{ color: '#888', fontSize: '13px', fontStyle: 'italic' }}>No GL keyword rules defined yet.</p>
            ) : (
              <table style={subTableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Keyword</th>
                    <th style={thStyle}>Operator</th>
                    <th style={thStyle}>GL #</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Priority</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Active</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {glKeywords.map(kw => (
                    <tr key={kw.id} style={{ backgroundColor: editingKwId === kw.id ? '#fffbeb' : 'white' }}>
                      <td style={tdStyle}>{kw.keyword}</td>
                      <td style={tdStyle}>
                        <span style={{ background: '#e8f0f7', padding: '2px 6px', borderRadius: '3px', fontSize: '11px' }}>
                          {kw.match_operator}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{kw.gl_number}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{kw.priority}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={activeDot(kw.active)}>{kw.active ? '✓' : '✗'}</span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button style={btnEdit} onClick={() => handleEditKw(kw)}>Edit</button>
                        <button style={btnDanger} onClick={() => handleDeleteKw(kw.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* GL Keyword Form */}
            <div style={inlineFormStyle}>
              <div style={{ fontWeight: 'bold', fontSize: '13px', color: NAVY, marginBottom: '12px' }}>
                {editingKwId !== null ? '✏️ Edit GL Keyword' : '＋ Add GL Keyword'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div>
                  <label style={labelStyle}>Keyword *</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={kwForm.keyword}
                    onChange={e => setKwForm({ ...kwForm, keyword: e.target.value })}
                    placeholder="e.g. PRODUCE"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Match Operator</label>
                  <select
                    style={{ ...inputStyle }}
                    value={kwForm.match_operator}
                    onChange={e => setKwForm({ ...kwForm, match_operator: e.target.value as GLKeywordForm['match_operator'] })}
                  >
                    <option value="equals">equals</option>
                    <option value="contains">contains</option>
                    <option value="starts_with">starts_with</option>
                    <option value="regex">regex</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>GL Number *</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={kwForm.gl_number}
                    onChange={e => setKwForm({ ...kwForm, gl_number: e.target.value })}
                    placeholder="e.g. 4100"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Priority <span style={{ fontWeight: 'normal', color: '#888' }}>(lower = higher priority)</span></label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={kwForm.priority}
                    onChange={e => setKwForm({ ...kwForm, priority: parseInt(e.target.value) || 100 })}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={kwForm.active}
                    onChange={e => setKwForm({ ...kwForm, active: e.target.checked })}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '12px', fontWeight: 'bold' }}>Active</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleSaveKw}
                  disabled={savingKw}
                  style={{ ...btnPrimary, opacity: savingKw ? 0.6 : 1, cursor: savingKw ? 'not-allowed' : 'pointer' }}
                >
                  {savingKw ? 'Saving...' : editingKwId !== null ? 'Update Keyword' : 'Add Keyword'}
                </button>
                {editingKwId !== null && (
                  <button onClick={handleCancelKw} style={btnSecondary}>Cancel</button>
                )}
              </div>
            </div>
            </div>{/* end greyed-out wrapper */}
          </div>

          {/* ── Section 3: Job Helper Keywords ── */}
          <div style={sectionHeaderStyle}>
            🗺️ Job Helper Keywords
            {!isSpecialProcessing && edits.job_resolution === 'default' && (
              <span style={{ marginLeft: '10px', fontSize: '11px', fontWeight: 'normal', opacity: 0.85, background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '10px' }}>
                Default mode — override job in use
              </span>
            )}
          </div>
          <div style={sectionBodyStyle}>
            {!isSpecialProcessing && edits.job_resolution === 'default' && (
              <div style={{ padding: '10px 14px', backgroundColor: '#fffbeb', border: '1px solid #d97706', borderRadius: '4px', marginBottom: '12px', fontSize: '12px', color: '#92400e' }}>
                ⚙️ <strong>Job Resolution is set to Default.</strong> The override job number set above is applied to every line. This keyword table is inactive and ignored during processing.
              </div>
            )}
            <div style={(!isSpecialProcessing && edits.job_resolution === 'default') ? { opacity: 0.35, pointerEvents: 'none' } : {}}>
            <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#555' }}>
              Maps invoice field values to job numbers. <strong>Field Label</strong> describes what is being matched
              (e.g. "Ship To Name", "Account Number"). <strong>Value</strong> is the text to match.
              <strong> Match Operator</strong> controls how: <em>equals</em> for exact codes, <em>contains</em> for names.
            </p>
            {jkLoading ? (
              <p style={{ color: '#666', fontSize: '13px' }}>Loading job keywords...</p>
            ) : jobKeywords.length === 0 ? (
              <p style={{ color: '#888', fontSize: '13px', fontStyle: 'italic' }}>No job keyword rules defined yet.</p>
            ) : (
              <table style={subTableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Field Label</th>
                    <th style={thStyle}>Value</th>
                    <th style={thStyle}>Operator</th>
                    <th style={thStyle}>Job #</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Priority</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Active</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobKeywords.map(jk => (
                    <tr key={jk.id} style={{ backgroundColor: editingJKId === jk.id ? '#fffbeb' : 'white' }}>
                      <td style={{ ...tdStyle, fontWeight: 'bold', color: '#374151' }}>{jk.keyword}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{jk.keywordvalue}</td>
                      <td style={tdStyle}>
                        <span style={{ background: '#e8f0f7', padding: '2px 6px', borderRadius: '3px', fontSize: '11px' }}>
                          {jk.match_operator}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 'bold', color: '#00386C' }}>{jk.job_number}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{jk.priority}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={activeDot(jk.active)}>{jk.active ? '✓' : '✗'}</span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button style={btnEdit} onClick={() => handleEditJK(jk)}>Edit</button>
                        <button style={btnDanger} onClick={() => handleDeleteJK(jk.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Job Keyword Form */}
            <div style={inlineFormStyle}>
              <div style={{ fontWeight: 'bold', fontSize: '13px', color: NAVY, marginBottom: '12px' }}>
                {editingJKId !== null ? '✏️ Edit Job Keyword' : '＋ Add Job Keyword'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div>
                  <label style={labelStyle}>Field Label * <span style={{ fontWeight: 'normal', color: '#888' }}>(e.g. "Ship To Name")</span></label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={jkForm.keyword}
                    onChange={e => setJKForm({ ...jkForm, keyword: e.target.value })}
                    placeholder="e.g. Ship To Name"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Value * <span style={{ fontWeight: 'normal', color: '#888' }}>(e.g. "DJ Jacobetti")</span></label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={jkForm.keywordvalue}
                    onChange={e => setJKForm({ ...jkForm, keywordvalue: e.target.value })}
                    placeholder="e.g. DJ Jacobetti"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Match Operator</label>
                  <select
                    style={{ ...inputStyle }}
                    value={jkForm.match_operator}
                    onChange={e => setJKForm({ ...jkForm, match_operator: e.target.value as JobKeywordForm['match_operator'] })}
                  >
                    <option value="contains">contains — name/address partial match</option>
                    <option value="equals">equals — exact code/ID match</option>
                    <option value="starts_with">starts_with — prefix match</option>
                    <option value="regex">regex — advanced pattern</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Job # *</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={jkForm.job_number}
                    onChange={e => setJKForm({ ...jkForm, job_number: e.target.value })}
                    placeholder="e.g. 3460"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Priority <span style={{ fontWeight: 'normal', color: '#888' }}>(lower = higher priority)</span></label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={jkForm.priority}
                    onChange={e => setJKForm({ ...jkForm, priority: parseInt(e.target.value) || 100 })}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Notes <span style={{ fontWeight: 'normal', color: '#888' }}>(optional)</span></label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={jkForm.notes}
                    onChange={e => setJKForm({ ...jkForm, notes: e.target.value })}
                    placeholder="e.g. Jacobetti Catering #2"
                  />
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={jkForm.active}
                    onChange={e => setJKForm({ ...jkForm, active: e.target.checked })}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '12px', fontWeight: 'bold' }}>Active</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleSaveJK}
                  disabled={savingJK}
                  style={{ ...btnPrimary, opacity: savingJK ? 0.6 : 1, cursor: savingJK ? 'not-allowed' : 'pointer' }}
                >
                  {savingJK ? 'Saving...' : editingJKId !== null ? 'Update Keyword' : 'Add Keyword'}
                </button>
                {editingJKId !== null && (
                  <button onClick={handleCancelJK} style={btnSecondary}>Cancel</button>
                )}
              </div>
            </div>
            </div>{/* end greyed-out wrapper */}
          </div>

          {/* ── Section 4: Parts ── */}
          <div style={sectionHeaderStyle}>📦 Parts / Item Lookup</div>
          <div style={sectionBodyStyle}>

            {/* Parts toolbar */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search by part #, description, or category..."
                value={partsSearch}
                onChange={e => setPartsSearch(e.target.value)}
                style={{ ...inputStyle, maxWidth: '300px', flex: '1 1 200px' }}
              />
              <button
                onClick={() => { setPartForm(EMPTY_PART_FORM); setEditingPartId(null); setShowPartForm(true); }}
                style={{ ...btnPrimary, whiteSpace: 'nowrap' }}
              >
                ＋ Add Part
              </button>
              <button
                onClick={() => csvInputRef.current?.click()}
                disabled={importingCSV}
                style={{
                  ...btnSecondary,
                  opacity: importingCSV ? 0.6 : 1,
                  cursor: importingCSV ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {importingCSV ? 'Importing...' : '⬆ CSV Import'}
              </button>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleCSVImport}
              />
              <span style={{ fontSize: '11px', color: '#888' }}>
                CSV columns: <code>part_number</code>, <code>gl_number</code> (required); <code>description</code>, <code>category</code>, <code>job_number</code> (optional)
              </span>
            </div>

            {partsLoading ? (
              <p style={{ color: '#666', fontSize: '13px' }}>Loading parts...</p>
            ) : filteredParts.length === 0 ? (
              <p style={{ color: '#888', fontSize: '13px', fontStyle: 'italic' }}>
                {partsSearch ? 'No parts match your search.' : 'No parts defined yet. Use "Add Part" or "CSV Import" to get started.'}
              </p>
            ) : (
              <>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>
                  Showing {filteredParts.length} of {parts.length} parts
                </div>
                <table style={subTableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Part #</th>
                      <th style={thStyle}>Description</th>
                      <th style={thStyle}>GL #</th>
                      <th style={thStyle}>Category</th>
                      <th style={thStyle}>Job #</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Active</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredParts.map(p => (
                      <tr key={p.id} style={{ backgroundColor: editingPartId === p.id ? '#fffbeb' : 'white' }}>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 'bold' }}>{p.part_number}</td>
                        <td style={{ ...tdStyle, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.description || <span style={{ color: '#bbb' }}>—</span>}
                        </td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{p.gl_number}</td>
                        <td style={tdStyle}>{p.category || <span style={{ color: '#bbb' }}>—</span>}</td>
                        <td style={tdStyle}>{p.job_number || <span style={{ color: '#bbb' }}>—</span>}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <span style={activeDot(p.active)}>{p.active ? '✓' : '✗'}</span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <button style={btnEdit} onClick={() => handleEditPart(p)}>Edit</button>
                          <button style={btnDanger} onClick={() => handleDeletePart(p.id)}>Deactivate</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Part Form */}
            {showPartForm && (
              <div style={inlineFormStyle}>
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: NAVY, marginBottom: '12px' }}>
                  {editingPartId !== null ? '✏️ Edit Part' : '＋ Add Part'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label style={labelStyle}>Part Number *</label>
                    <input
                      type="text"
                      style={inputStyle}
                      value={partForm.part_number}
                      onChange={e => setPartForm({ ...partForm, part_number: e.target.value })}
                      placeholder="e.g. 123456"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>GL Number *</label>
                    <input
                      type="text"
                      style={inputStyle}
                      value={partForm.gl_number}
                      onChange={e => setPartForm({ ...partForm, gl_number: e.target.value })}
                      placeholder="e.g. 4085"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Description</label>
                    <input
                      type="text"
                      style={inputStyle}
                      value={partForm.description}
                      onChange={e => setPartForm({ ...partForm, description: e.target.value })}
                      placeholder="e.g. Copy Paper Letter"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Category</label>
                    <input
                      type="text"
                      style={inputStyle}
                      value={partForm.category}
                      onChange={e => setPartForm({ ...partForm, category: e.target.value })}
                      placeholder="e.g. Office Supplies"
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Job # <span style={{ fontWeight: 'normal', color: '#888' }}>(optional override)</span></label>
                    <input
                      type="text"
                      style={inputStyle}
                      value={partForm.job_number}
                      onChange={e => setPartForm({ ...partForm, job_number: e.target.value })}
                      placeholder="e.g. 3460"
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={partForm.active}
                        onChange={e => setPartForm({ ...partForm, active: e.target.checked })}
                        style={{ cursor: 'pointer', width: '15px', height: '15px' }}
                      />
                      <span style={{ fontSize: '12px', fontWeight: 'bold' }}>Active</span>
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleSavePart}
                    disabled={savingPart}
                    style={{ ...btnPrimary, opacity: savingPart ? 0.6 : 1, cursor: savingPart ? 'not-allowed' : 'pointer' }}
                  >
                    {savingPart ? 'Saving...' : editingPartId !== null ? 'Update Part' : 'Add Part'}
                  </button>
                  <button onClick={handleCancelPart} style={btnSecondary}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Section 5: AI Processing Notes ── */}
          {!isSpecialProcessing && (
            <>
              <div style={sectionHeaderStyle}>🤖 AI Processing Notes</div>
              <div style={sectionBodyStyle}>
                <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#555', lineHeight: '1.5' }}>
                  Plain-text instructions sent to the AI when processing invoices from this vendor.
                  Describe unusual layouts, how to find job numbers, lines to ignore, amount quirks, etc.
                </p>
                <textarea
                  value={edits.processing_instructions}
                  onChange={e => setEdits({ ...edits, processing_instructions: e.target.value })}
                  placeholder={`e.g. "Reference column contains the job number as the digits before the first hyphen (e.g. 3457-TSO = job 3457). If the total shows $0.00 due to a credit card payment line, use the sum of the individual charges as invoice_amount and do not classify as a credit memo."`}
                  rows={5}
                  style={{
                    width: '100%', padding: '8px 10px',
                    border: '1px solid #d0dbe8', borderRadius: '4px',
                    fontSize: '13px', fontFamily: 'inherit',
                    resize: 'vertical', boxSizing: 'border-box',
                    color: '#1a202c', outline: 'none', lineHeight: '1.5',
                  }}
                />
                <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#888' }}>
                  Saved with vendor settings — click <strong>Save Vendor</strong> above to apply.
                </p>
              </div>
            </>
          )}

        </div>{/* end scrollable */}
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '820px',
            backgroundColor: toast.type === 'success' ? '#2d7a2d' : '#c0392b',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 'bold',
            zIndex: 1100,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            maxWidth: '300px',
          }}
        >
          {toast.message}
        </div>
      )}
    </>
  );
};

export default VendorDetailPanel;
